import React, { useEffect, useRef } from 'react'
import { FloraVariants, forwardRef, styled } from '@grupoboticario/flora-react-system'
import { HTMLFloraProps, mergeRefs } from '@grupoboticario/flora-react-utils'
import { FloraCheckbox } from '@grupoboticario/flora-react-checkbox'
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu'

export const StyledDropdownContent = styled(DropdownMenuPrimitive.Content, {
  backgroundColor: '$backgroundPrimary',
  flexDirection: 'column',
  borderRadius: '$small',
  boxShadow: '$downSoft',
  marginTop: '$2',
  border: '1px solid $nonInteractiveOutline',
  '&:focus': {
    outline: 'none',
  },
  variants: {
    variant: {
      alt: {
        backgroundColor: '$backgroundAltSecondary',
        color: '$nonInteractiveAltPredominant',
      },
    },
  },
})

export const StyledDropdownItem = styled(DropdownMenuPrimitive.Item, {
  display: 'flex',
  cursor: 'pointer',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '$4',
  justifyContent: 'left',
  width: '100%',
  height: 56,
  padding: 12,
  border: 'none',
  borderTop: '1px solid $nonInteractiveOutline',
  backgroundColor: '$backgroundPrimary',
  '&:hover': {
    backgroundColor: '$backgroundSecondary',
  },
  '&[data-disabled]': {
    pointerEvents: 'none',
    backgroundColor: '$disabledBackground',
  },
  variants: {
    variant: {
      alt: {
        backgroundColor: '$backgroundAltSecondary',
        borderTop: '1px solid $nonInteractiveAltOutline',
        '&:hover': {
          backgroundColor: '$backgroundAltPrimary',
        },
      },
    },
    checked: {
      true: {
        backgroundColor: '$backgroundSecondary',
      },
    },
    multiple: {
      true: {
        position: 'relative',
        '&:before': {
          content: '',
          position: 'absolute',
          top: 0,
          left: 0,
          width: '40px',
          height: '100%',
          opacity: 0,
          zIndex: 1,
        },
      },
    },
  },
  compoundVariants: [
    {
      variant: 'alt',
      checked: true,
      css: {
        backgroundColor: '$backgroundAltPrimary',
      },
    },
  ],
}) as React.ElementType

export type DropdownItemProps = Omit<HTMLFloraProps<'option'>, 'value' | 'checked'> & {
  value?: string | number | boolean
  checked?: boolean
  multiple?: boolean
  children: React.ReactNode
  variant?: any
}

/**
 * @tutorial https://flora.gb.tech/docs/components/core/dropdown
 */
export const DropdownItem = forwardRef<'option', DropdownItemProps>(
  ({ children, checked = false, multiple = false, ...props }, forwardedRef) => {
    const itemRef = useRef<HTMLOptionElement>()

    useEffect(() => {
      // when item selected, scroll to and focus on it
      if (props['aria-selected'] === 'true') {
        itemRef.current?.scrollIntoView?.()
        itemRef.current?.focus()
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return (
      <StyledDropdownItem
        ref={mergeRefs(forwardedRef, itemRef)}
        {...props}
        checked={checked}
        multiple={multiple}
      >
        {multiple && <FloraCheckbox id="checkbox-checked" checked={checked} readOnly />}
        {children}
      </StyledDropdownItem>
    )
  },
)

export const SelectNativeInput = styled('input', {
  bottom: 0,
  left: 0,
  position: 'absolute',
  opacity: 0,
  pointerEvents: 'none',
  width: '100%',
  boxSizing: 'border-box',
})

const StyledIcon = styled('svg', {
  transition: 'transform 175ms cubic-bezier(0.65, 0, 0.35, 1)',
  cursor: 'pointer',
  variants: {
    open: {
      true: {
        transform: 'rotate(180deg)',
      },
    },
  },
})

export const DropdownIcon = forwardRef<'svg', FloraVariants<typeof StyledIcon>>(
  (props, forwardedRef) => {
    return (
      <StyledIcon
        {...props}
        ref={forwardedRef}
        width="12"
        height="8"
        viewBox="0 0 12 8"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path d="M1.41.59L6 5.17 10.59.59 12 2 6 8 0 2 1.41.59z" fill="currentColor" />
      </StyledIcon>
    )
  },
)
